/**
 * A tela Usuários (história 11.1) — o CRUD de usuário da 1.5 com o perfil de cada um.
 *
 * Mora, até a 11.2, como aba da Administração; a 11.2 a leva para `/configuracao` sem reescrita.
 * Colunas: nome, e-mail, perfil (o nome de exibição do perfil do catálogo), a situação do
 * WhatsApp (de `GET /numeros` do `comunicacao` — quem nunca conectou não tem linha, e a célula
 * mostra "—") e ativo/desativado. Ações: criar, trocar o perfil e desativar.
 *
 * **Só o admin de fábrica vê a tela** (`ehAdminDeFabrica`): as rotas que ela lê respondem `403`
 * a qualquer outro perfil — inclusive a um personalizado derivado de Admin —, e o componente
 * devolve `null` sem disparar consulta nenhuma. Esconder é conveniência: quem autoriza é o
 * serviço (AD-18).
 *
 * **Trocar o perfil derruba as sessões da pessoa** (AD-5), e a confirmação diz isso antes do
 * clique. As mensagens de recusa aparecem como o serviço as mandou.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useSituacaoDoWhatsapp, type SituacaoDoWhatsapp } from '@/modulos/comunicacao/api'
import { Dialogo } from '@/modulos/negocios/Dialogo'
import {
  CHAVE_DOS_PERFIS,
  CHAVE_DOS_RESPONSAVEIS,
  CHAVE_DOS_USUARIOS,
  type PerfilDoCatalogo,
  type Usuario,
  criarUsuario,
  desativarUsuario,
  ehAdminDeFabrica,
  trocarPerfilDoUsuario,
  usePerfis,
  useUsuarios,
} from './api'
import { useSair, useSessao } from './sessao'
import './gestao-de-acesso.css'

const ID_DO_TITULO = 'titulo-dos-usuarios'

/** Desde a 13.21a1 a `situacao` é anulável (a forma reduzida de quem entra só pelos Funis); esta
 * tela é do admin de fábrica, que recebe sempre a forma completa — o nulo vira "—". */
const ROTULO_DA_SITUACAO: Record<NonNullable<SituacaoDoWhatsapp['situacao']>, string> = {
  conectado: 'Conectado',
  pareando: 'Pareando',
  desconectado: 'Desconectado',
}

/** Qual diálogo está aberto — um de cada vez (o modal empilha um nível só). */
type DialogoAberto =
  | { tipo: 'novo' }
  | { tipo: 'trocar'; usuario: Usuario }
  | { tipo: 'desativar'; usuario: Usuario }
  | null

function SituacaoDoWhatsappNaCelula({
  situacao,
  indisponivel,
}: {
  situacao: SituacaoDoWhatsapp | undefined
  indisponivel: boolean
}) {
  if (indisponivel) {
    // A leitura falhou: "—" diria "nunca conectou", que não é o que se sabe.
    return <span className="acesso__apagado">indisponível</span>
  }
  if (situacao === undefined || situacao.situacao === null) {
    return <span className="acesso__apagado">—</span>
  }
  return (
    <span className={`acesso__situacao--${situacao.situacao}`}>
      {ROTULO_DA_SITUACAO[situacao.situacao]}
    </span>
  )
}

function NovoUsuario({ perfis, aoFechar }: { perfis: PerfilDoCatalogo[]; aoFechar: () => void }) {
  const clienteDeConsultas = useQueryClient()
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [perfilId, setPerfilId] = useState(
    perfis.find((perfil) => perfil.de_fabrica && perfil.perfil_base === 'vendedor_closer')?.id ??
      perfis[0]?.id ??
      '',
  )
  const criacao = useMutation({
    mutationFn: () => criarUsuario({ nome, email, senha, perfil_id: perfilId }),
    onSuccess: async () => {
      await Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_USUARIOS }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_PERFIS }),
        clienteDeConsultas.invalidateQueries({
          queryKey: CHAVE_DOS_RESPONSAVEIS,
        }),
      ])
      aoFechar()
    },
  })
  const incompleto = nome.trim() === '' || email.trim() === '' || senha === '' || perfilId === ''

  return (
    <Dialogo
      titulo="Novo usuário"
      subtitulo="A pessoa entra com esta senha inicial. As permissões vêm do perfil."
      rotuloDoConfirmar="Criar usuário"
      confirmarDesabilitado={incompleto}
      ocupado={criacao.isPending}
      erro={criacao.isError ? criacao.error : undefined}
      aoFechar={aoFechar}
      aoConfirmar={() => {
        criacao.mutate()
      }}
    >
      <label className="acesso__campo">
        <span className="acesso__rotulo">Nome</span>
        <input
          className="acesso__entrada"
          value={nome}
          maxLength={150}
          onChange={(evento) => {
            setNome(evento.target.value)
          }}
        />
      </label>
      <label className="acesso__campo">
        <span className="acesso__rotulo">E-mail</span>
        <input
          className="acesso__entrada"
          type="email"
          value={email}
          maxLength={320}
          onChange={(evento) => {
            setEmail(evento.target.value)
          }}
        />
      </label>
      <label className="acesso__campo">
        <span className="acesso__rotulo">Senha inicial</span>
        <input
          className="acesso__entrada"
          type="password"
          value={senha}
          maxLength={1024}
          autoComplete="new-password"
          onChange={(evento) => {
            setSenha(evento.target.value)
          }}
        />
      </label>
      <label className="acesso__campo">
        <span className="acesso__rotulo">Perfil</span>
        <select
          className="acesso__entrada"
          value={perfilId}
          onChange={(evento) => {
            setPerfilId(evento.target.value)
          }}
        >
          {perfis.map((perfil) => (
            <option key={perfil.id} value={perfil.id}>
              {perfil.nome}
            </option>
          ))}
        </select>
      </label>
      <p className="acesso__nota">
        Para um conjunto diferente de permissões, crie um perfil personalizado na aba Permissões.
      </p>
    </Dialogo>
  )
}

function TrocarPerfil({
  usuario,
  perfis,
  souEu,
  aoFechar,
}: {
  usuario: Usuario
  perfis: PerfilDoCatalogo[]
  souEu: boolean
  aoFechar: () => void
}) {
  const clienteDeConsultas = useQueryClient()
  const sair = useSair()
  const [perfilId, setPerfilId] = useState(usuario.perfil_id)
  const troca = useMutation({
    mutationFn: () => trocarPerfilDoUsuario(usuario.id, perfilId),
    onSuccess: async () => {
      if (souEu) {
        // Trocar o próprio perfil: o serviço já derrubou as sessões (AD-5); ficar na tela seria
        // tomar `401` a cada clique. Sair é o honesto, como na desativação.
        sair.mutate()
        return
      }
      await Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_USUARIOS }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_PERFIS }),
      ])
      aoFechar()
    },
  })

  return (
    <Dialogo
      titulo="Trocar perfil"
      subtitulo={usuario.nome}
      rotuloDoConfirmar="Trocar perfil"
      confirmarDesabilitado={perfilId === usuario.perfil_id}
      ocupado={troca.isPending}
      erro={troca.isError ? troca.error : undefined}
      aoFechar={aoFechar}
      aoConfirmar={() => {
        troca.mutate()
      }}
    >
      <label className="acesso__campo">
        <span className="acesso__rotulo">Perfil</span>
        <select
          className="acesso__entrada"
          value={perfilId}
          onChange={(evento) => {
            setPerfilId(evento.target.value)
          }}
        >
          {perfis.map((perfil) => (
            <option key={perfil.id} value={perfil.id}>
              {perfil.nome}
            </option>
          ))}
        </select>
      </label>
      <p className="acesso__aviso">
        Trocar o perfil encerra as sessões de {usuario.nome}: a pessoa precisa entrar de novo.
      </p>
    </Dialogo>
  )
}

function Desativar({
  usuario,
  souEu,
  aoFechar,
}: {
  usuario: Usuario
  souEu: boolean
  aoFechar: () => void
}) {
  const clienteDeConsultas = useQueryClient()
  const sair = useSair()
  const desativacao = useMutation({
    mutationFn: () => desativarUsuario(usuario.id),
    onSuccess: async () => {
      if (souEu) {
        // Desativar a si mesmo: a gestão de acesso passa a recusar na hora; sair é o honesto.
        sair.mutate()
        return
      }
      await Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_USUARIOS }),
        // O seletor de Responsável também mostra quem está ativo.
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_RESPONSAVEIS }),
      ])
      aoFechar()
    },
  })

  return (
    <Dialogo
      titulo="Desativar usuário"
      subtitulo={usuario.nome}
      rotuloDoConfirmar="Desativar"
      ocupado={desativacao.isPending}
      erro={desativacao.isError ? desativacao.error : undefined}
      aoFechar={aoFechar}
      aoConfirmar={() => {
        desativacao.mutate()
      }}
    >
      <p className="acesso__nota">
        A pessoa deixa de entrar e as sessões dela caem agora. O nome continua no histórico, e não
        há como reativar pela tela.
      </p>
    </Dialogo>
  )
}

export function Usuarios() {
  const { data: sessao } = useSessao()
  const podeVer = sessao !== undefined && ehAdminDeFabrica(sessao)
  const usuarios = useUsuarios({ habilitada: podeVer })
  const perfis = usePerfis({ habilitada: podeVer })
  const situacoes = useSituacaoDoWhatsapp({ habilitada: podeVer })
  const [dialogo, setDialogo] = useState<DialogoAberto>(null)

  if (!podeVer) {
    return null
  }

  const listaDePerfis = perfis.data ?? []
  const situacaoDe = new Map(
    (situacoes.data ?? []).map((situacao) => [situacao.usuario_id, situacao]),
  )
  const ativos = (usuarios.data ?? []).filter((usuario) => usuario.ativo).length
  const fechar = () => {
    setDialogo(null)
  }

  return (
    <section className="acesso" aria-labelledby={ID_DO_TITULO}>
      <div className="acesso__cartao">
        <div className="acesso__cabecalho">
          <div className="acesso__cabecalho-texto">
            <h2 className="acesso__titulo" id={ID_DO_TITULO}>
              Usuários
            </h2>
            {usuarios.data === undefined ? null : (
              <span className="acesso__nota">
                <span className="numero">{ativos}</span> ativos ·{' '}
                <span className="numero">{usuarios.data.length - ativos}</span> desativado(s)
              </span>
            )}
          </div>
          <button
            type="button"
            className="acesso__botao"
            disabled={listaDePerfis.length === 0}
            onClick={() => {
              setDialogo({ tipo: 'novo' })
            }}
          >
            + Novo usuário
          </button>
        </div>
        <div className="acesso__corpo">
          {perfis.isError ? (
            <p className="acesso__erro" role="alert">
              Não foi possível ler os perfis, e por isso criar usuário e trocar perfil estão
              desabilitados: {perfis.error.message}
            </p>
          ) : null}
          {situacoes.isError ? (
            <p className="acesso__erro" role="alert">
              Não foi possível ler a situação do WhatsApp: {situacoes.error.message}
            </p>
          ) : null}
          {usuarios.isError ? (
            <p className="acesso__erro" role="alert">
              {usuarios.error.message}{' '}
              <button
                type="button"
                className="acesso__botao acesso__botao--discreto"
                onClick={() => {
                  void usuarios.refetch()
                }}
              >
                Tentar de novo
              </button>
            </p>
          ) : usuarios.data === undefined ? (
            <p className="acesso__nota" role="status">
              Carregando usuários…
            </p>
          ) : (
            <div className="acesso__rolagem">
              <table className="acesso__tabela" aria-label="Usuários">
                <thead>
                  <tr>
                    <th scope="col">Nome</th>
                    <th scope="col">E-mail</th>
                    <th scope="col">Perfil</th>
                    <th scope="col">WhatsApp</th>
                    <th scope="col">Situação</th>
                    <th scope="col" aria-label="Ações" />
                  </tr>
                </thead>
                <tbody>
                  {usuarios.data.map((usuario) => (
                    <tr
                      key={usuario.id}
                      className={usuario.ativo ? undefined : 'acesso__linha--desativada'}
                    >
                      <td className="acesso__nome">{usuario.nome}</td>
                      <td className="acesso__apagado">{usuario.email}</td>
                      <td>{usuario.nome_do_perfil}</td>
                      <td>
                        <SituacaoDoWhatsappNaCelula
                          situacao={situacaoDe.get(usuario.id)}
                          indisponivel={situacoes.isError}
                        />
                      </td>
                      <td>
                        <span
                          className={`acesso__estado ${
                            usuario.ativo ? 'acesso__estado--ativo' : 'acesso__estado--desativado'
                          }`}
                        >
                          {usuario.ativo ? 'ativo' : 'desativado'}
                        </span>
                      </td>
                      <td>
                        {usuario.ativo ? (
                          <div className="acesso__acoes">
                            <button
                              type="button"
                              className="acesso__botao acesso__botao--discreto"
                              disabled={listaDePerfis.length === 0}
                              aria-label={`Trocar perfil de ${usuario.nome}`}
                              onClick={() => {
                                setDialogo({ tipo: 'trocar', usuario })
                              }}
                            >
                              Trocar perfil
                            </button>
                            <button
                              type="button"
                              className="acesso__botao acesso__botao--perigo"
                              aria-label={`Desativar ${usuario.nome}`}
                              onClick={() => {
                                setDialogo({ tipo: 'desativar', usuario })
                              }}
                            >
                              Desativar…
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {dialogo?.tipo === 'novo' ? <NovoUsuario perfis={listaDePerfis} aoFechar={fechar} /> : null}
      {dialogo?.tipo === 'trocar' ? (
        <TrocarPerfil
          usuario={dialogo.usuario}
          perfis={listaDePerfis}
          souEu={dialogo.usuario.id === sessao.usuario_id}
          aoFechar={fechar}
        />
      ) : null}
      {dialogo?.tipo === 'desativar' ? (
        <Desativar
          usuario={dialogo.usuario}
          souEu={dialogo.usuario.id === sessao.usuario_id}
          aoFechar={fechar}
        />
      ) : null}
    </section>
  )
}
